import { Component, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import AppShell from './AppShell';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <AppShell>
          <div className="max-w-md mx-auto px-4 py-24 text-center">
            <p className="text-5xl mb-4">&#x26A0;&#xFE0F;</p>
            <h1 className="text-2xl font-bold tracking-tight">
              Something went wrong
            </h1>
            <p className="mt-2 text-slate-400">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false });
                window.location.href = '/';
              }}
              className="mt-6 inline-flex items-center px-5 py-2.5 rounded-full gradient-cta text-white font-semibold text-sm glow-primary transition-all transform hover:-translate-y-0.5"
            >
              Back to home
            </button>
          </div>
        </AppShell>
      );
    }

    return this.props.children;
  }
}
